/**
 * <media-carousel>
 *
 * The base Horizon `slideshow` component is built for one-slide-at-a-time
 * galleries: it renders one dot per slide and its active-dot tracking follows
 * the centred slide. In this block several cards are visible at once ("peek"
 * carousel), so per-slide dots leave the trailing cards with unreachable dots
 * (you can never centre them, so clicking does nothing).
 *
 * This element replaces those dots with page-based dots: dot count =
 * ceil(cards / cardsPerView). Clicking a dot scrolls the scroller to that
 * page, and the active dot is derived from the scroll position so every dot is
 * meaningful and reachable. It owns only the dots; scrolling, drag and snap are
 * still handled by the underlying <slideshow-component>.
 *
 * Infinite mode: permanent clone zones are inserted on both ends so the user
 * can scroll freely through the clone territory. On `scrollend`, if the final
 * position is inside a clone zone we silently jump by exactly one full real-
 * cards-width to land on the equivalent real card.
 *
 * DOM layout when infinite:
 *   [head clones: last N real cards] [real cards] [tail clones: first N real cards]
 *
 * The scroll distance from real-card-0's snap position to tail-clone-0's snap
 * position is exactly realCount × step, so the correction is a clean integer
 * multiple of the card step.
 */
class MediaCarousel extends HTMLElement {
  #building = false;
  // Infinite-mode layout metrics (reset to 0 when clones are absent)
  #infiniteOffset = 0;    // scrollLeft that centers real card 0
  #infiniteRealEnd = 0;   // scrollLeft that centers real card last
  #infiniteRealWidth = 0; // realCount × step (correction jump distance)

  get infinite() {
    return this.querySelector('slideshow-component')?.hasAttribute('infinite') ?? false;
  }

  connectedCallback() {
    this.scroller = this.querySelector('slideshow-slides');
    this.controls = this.querySelector('slideshow-controls');
    this.dotsList = this.querySelector('.slideshow-controls__dots');
    this.prevArrow = this.querySelector('.slideshow-control--previous');
    this.nextArrow = this.querySelector('.slideshow-control--next');

    if (!this.scroller || !this.dotsList) return;

    this.labelTemplate = this.getAttribute('data-dot-label') || 'Slide %index% of %length%';

    this.handleScroll = this.rafThrottle(() => this.updateActiveDot());
    this.scroller.addEventListener('scroll', this.handleScroll, { passive: true });

    this.handleScrollEnd = () => this.#correctInfinitePosition();
    this.scroller.addEventListener('scrollend', this.handleScrollEnd, { passive: true });

    // Capture arrow clicks before the native slideshow handler so they step one
    // card at a time instead of centering a slide visibleSlides away.
    this.addEventListener('click', this.handleArrowClick, true);

    this.resizeObserver = new ResizeObserver(() => this.build());
    this.resizeObserver.observe(this.scroller);

    this.build();
  }

  disconnectedCallback() {
    this.scroller?.removeEventListener('scroll', this.handleScroll);
    this.scroller?.removeEventListener('scrollend', this.handleScrollEnd);
    this.removeEventListener('click', this.handleArrowClick, true);
    this.resizeObserver?.disconnect();
    this.#removeClones();
  }

  /**
   * Step one card per arrow click, taking over the native slide-centering handler.
   * In infinite mode, scroll freely past the boundaries — the scrollend handler
   * will silently land us on the equivalent real card if we hit a clone zone.
   * @param {MouseEvent} event
   */
  handleArrowClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const arrow = event.target.closest('.slideshow-control--next, .slideshow-control--previous');
    if (!arrow || !this.contains(arrow)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const direction = arrow.classList.contains('slideshow-control--next') ? 1 : -1;
    const cards = this.scroller.querySelectorAll(':scope > .card:not(.mc-clone)');
    if (!cards.length) return;

    const cardWidth = cards[0].getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
    const step = cardWidth + gap;
    const scrollLeft = this.scroller.scrollLeft;

    if (this.infinite) {
      this.scroller.scrollTo({ left: scrollLeft + direction * step, behavior: 'smooth' });
    } else {
      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
      const target = Math.min(Math.max(scrollLeft + direction * step, 0), maxScroll);
      this.scroller.scrollTo({ left: target, behavior: 'smooth' });
    }
  };

  /**
   * After every scroll settles, check whether we landed in a clone zone and
   * silently jump to the equivalent real-card position.
   */
  #correctInfinitePosition() {
    if (!this.infinite || this.#infiniteRealWidth === 0) return;
    const sl = this.scroller.scrollLeft;
    let target = null;

    if (sl < this.#infiniteOffset - 1) {
      // Landed in head-clone zone → jump forward by one full real-cards width
      target = sl + this.#infiniteRealWidth;
    } else if (sl > this.#infiniteRealEnd + 1) {
      // Landed in tail-clone zone → jump backward by one full real-cards width
      target = sl - this.#infiniteRealWidth;
    }
    if (target === null) return;

    // scrollTo({ behavior:'instant' }) overrides CSS scroll-behavior:smooth.
    // Disable scroll-snap first so the browser doesn't intercept the jump.
    this.scroller.style.scrollSnapType = 'none';
    this.scroller.getBoundingClientRect(); // force layout flush
    this.scroller.scrollTo({ left: target, behavior: 'instant' });
    this.scroller.getBoundingClientRect(); // commit the new position
    this.scroller.style.scrollSnapType = ''; // restore CSS snap
  }

  /**
   * Insert permanent clone zones on both ends of the scroller.
   * N = perView cards cloned on each side so there is always at least one
   * full visible-width of clone territory before hitting the scroll boundary.
   *
   * DOM order: [head clones: last-N..last-1] [real cards] [tail clones: first-0..first-N-1]
   *
   * @param {Element[]} realCards
   * @param {number} perView
   * @param {number} step  cardWidth + gap
   */
  async #setupInfiniteClones(realCards, perView, step) {
    const N = perView;
    const count = realCards.length;

    // --- Tail clones: copies of first N real cards, appended after last real card ---
    for (let i = 0; i < N; i++) {
      const clone = realCards[i % count].cloneNode(true);
      clone.classList.add('mc-clone');
      clone.setAttribute('aria-hidden', 'true');
      this.scroller.appendChild(clone);
    }

    // --- Head clones: copies of last N real cards, prepended before first real card ---
    // Insert in natural order so the clone zone keeps the real sequence:
    // [..., last-N, ..., last-1, real_0, ...].
    for (let k = 0; k < N; k++) {
      const srcIdx = ((count - N + k) % count + count) % count;
      const clone = realCards[srcIdx].cloneNode(true);
      clone.classList.add('mc-clone');
      clone.setAttribute('aria-hidden', 'true');
      this.scroller.insertBefore(clone, realCards[0]);
    }

    // Wait one frame: browsers with scroll anchoring auto-adjust scrollLeft when
    // content is prepended, so we must read positions after the adjustment.
    await new Promise(r => requestAnimationFrame(r));

    // Compute the scrollLeft that snap-centers real card 0.
    const sr = this.scroller.getBoundingClientRect();
    const cr = realCards[0].getBoundingClientRect();
    const clientWidth = this.scroller.clientWidth;
    const cardWidth = cr.width;
    this.#infiniteOffset = Math.max(
      0,
      this.scroller.scrollLeft + (cr.left - sr.left) - (clientWidth / 2 - cardWidth / 2)
    );
    this.#infiniteRealWidth = count * step;
    this.#infiniteRealEnd = this.#infiniteOffset + (count - 1) * step;

    // Land on real card 0
    this.scroller.scrollTo({ left: this.#infiniteOffset, behavior: 'instant' });
  }

  #removeClones() {
    this.scroller?.querySelectorAll('.mc-clone').forEach(el => el.remove());
    this.#infiniteOffset = 0;
    this.#infiniteRealEnd = 0;
    this.#infiniteRealWidth = 0;
  }

  /** @returns {{ pages: number, maxScroll: number } | null} */
  getMetrics() {
    const cards = this.scroller.querySelectorAll(':scope > .card:not(.mc-clone)');
    if (!cards.length) return null;

    const clientWidth = this.scroller.clientWidth;
    const cardWidth = cards[0].getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
    const step = cardWidth + gap;
    const perView = Math.max(1, Math.floor((clientWidth + gap) / step));
    const pages = Math.max(1, Math.ceil(cards.length / perView));

    // In infinite mode the real card zone spans (count-1) steps.
    // In non-infinite mode use the actual scroll range.
    const maxScroll = (this.infinite && this.#infiniteRealWidth > 0)
      ? (cards.length - 1) * step
      : this.scroller.scrollWidth - clientWidth;

    return { pages, maxScroll };
  }

  /**
   * The page index for the current scroll position.
   * @param {{ pages: number, maxScroll: number }} metrics
   * @returns {number}
   */
  currentPage(metrics) {
    const { pages, maxScroll } = metrics;
    if (maxScroll <= 0) return 0;
    let sl = this.scroller.scrollLeft;
    // Subtract the clone-zone offset so the ratio is relative to real card 0.
    if (this.infinite && this.#infiniteOffset > 0) {
      sl = Math.max(0, sl - this.#infiniteOffset);
    }
    return Math.round((sl / maxScroll) * (pages - 1));
  }

  /**
   * Set the arrows' disabled state.
   * @param {boolean} prevDisabled
   * @param {boolean} nextDisabled
   */
  setArrowsDisabled(prevDisabled, nextDisabled) {
    if (this.prevArrow instanceof HTMLButtonElement) this.prevArrow.disabled = prevDisabled;
    if (this.nextArrow instanceof HTMLButtonElement) this.nextArrow.disabled = nextDisabled;
  }

  /**
   * (Re)build clone zones and page dots.
   * Always tears down existing clones first so measurements are taken on a
   * clean DOM. Guard prevents concurrent async calls from ResizeObserver.
   */
  async build() {
    if (this.#building) return;
    this.#building = true;

    try {
      // Start clean so getBoundingClientRect readings are accurate.
      this.#removeClones();
      await new Promise(r => requestAnimationFrame(r));

      const realCards = [...this.scroller.querySelectorAll(':scope > .card')];
      if (!realCards.length) return;

      const clientWidth = this.scroller.clientWidth;
      const cardWidth = realCards[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
      const step = cardWidth + gap;
      const perView = Math.max(1, Math.floor((clientWidth + gap) / step));
      const pages = Math.max(1, Math.ceil(realCards.length / perView));

      if (this.infinite && realCards.length >= 2 && pages > 1) {
        await this.#setupInfiniteClones(realCards, perView, step);
      }

      const metrics = this.getMetrics();
      if (!metrics) return;

      const shouldHide = metrics.pages <= 1 || metrics.maxScroll <= 1;
      if (this.controls) this.controls.hidden = shouldHide;

      if (shouldHide) {
        this.dotsList.replaceChildren();
        this.setArrowsDisabled(true, true);
        return;
      }

      // Only rebuild dot buttons when the page count changes (e.g. breakpoint switch).
      if (this.dotsList.children.length !== metrics.pages) {
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < metrics.pages; i++) {
          const item = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'slideshow-control button button-unstyled';
          button.setAttribute(
            'aria-label',
            this.labelTemplate.replace('%index%', `${i + 1}`).replace('%length%', `${metrics.pages}`)
          );
          button.addEventListener('click', () => this.goToPage(i));
          item.appendChild(button);
          fragment.appendChild(item);
        }
        this.dotsList.replaceChildren(fragment);
      }

      this.updateActiveDot();
    } finally {
      this.#building = false;
    }
  }

  /**
   * Scroll to the given page. In infinite mode the target is offset by the
   * head-clone zone width so we land on a real card, not a clone.
   * @param {number} page
   */
  goToPage(page) {
    const metrics = this.getMetrics();
    if (!metrics) return;
    const { pages, maxScroll } = metrics;
    const relLeft = pages > 1 ? (page / (pages - 1)) * maxScroll : 0;
    const left = this.infinite ? relLeft + this.#infiniteOffset : relLeft;
    this.scroller.scrollTo({ left, behavior: 'smooth' });
  }

  /** Derive the active page from the current scroll position. */
  updateActiveDot() {
    const metrics = this.getMetrics();
    if (!metrics) return;

    const active = this.currentPage(metrics);

    for (let i = 0; i < this.dotsList.children.length; i++) {
      const button = this.dotsList.children[i].firstElementChild;
      if (button) button.setAttribute('aria-selected', `${i === active}`);
    }

    if (this.infinite) {
      this.setArrowsDisabled(false, false);
    } else {
      this.setArrowsDisabled(active <= 0, active >= metrics.pages - 1);
    }
  }

  /**
   * Run `callback` at most once per animation frame.
   * @param {() => void} callback
   * @returns {() => void}
   */
  rafThrottle(callback) {
    let ticking = false;
    return () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        callback();
      });
    };
  }
}

if (!customElements.get('media-carousel')) {
  customElements.define('media-carousel', MediaCarousel);
}
